// src/pages/CreateProduct/productsParser.js
import * as XLSX from "xlsx";
import Papa from "papaparse";
import ExcelJS from "exceljs";

/* normalize */
const norm = (s = "") => s.toString().trim().toLowerCase().replace(/\s+|_/g, "");

/* Aliases (EN + AR) - Updated to match backend expected fields */
const HEADER_MAP = {
  sku: ["sku", "productcode", "product-code", "code", "item-code", "itemcode", "barcode", "رمزالمنتج", "الرمز", "كودالمنتج"],
  name_en: ["name", "productname", "product-name", "title", "itemname", "item-name", "الاسم", "اسمالمنتج", "العنوان"],
  name_ar: ["namear", "name-ar", "arabicname", "arabic-name", "الاسمبالعربية", "الاسمالعربي", "اسمعربي"],
  description_en: ["description", "desc", "details", "summary", "الوصف", "التفاصيل", "الملخص"],
  description_ar: ["descriptionar", "description-ar", "arabicdescription", "arabic-description", "الوصفبالعربية", "الوصفالعربي", "وصفعربي"],
  price: ["price", "cost", "amount", "unitprice", "unit-price", "السعر", "التكلفة", "المبلغ"],
  stock: ["stock", "quantity", "qty", "inventory", "available", "الكمية", "المخزون", "متوفر"],
  min_order: ["minorder", "min-order", "minimum", "minimumorder", "minimum-order", "الحدالأدنى", "اقلطلب", "الحدالادنىللطلب"],
  shipping_period: ["shippingperiod", "shipping-period", "deliverytime", "delivery-time", "مدةالشحن", "وقتالتوصيل"],
  category_id: ["categoryid", "category-id", "category", "categoryname", "cat-id", "معرفالفئة", "الفئة", "فئة"],
  offer_type: ["offertype", "offer-type", "type", "producttype", "product-type", "نوعالعرض", "النوع", "نوعالمنتج"],
  images: ["images", "image", "photos", "photo", "pictures", "picture", "imageurl", "الصور", "صورة", "الصورة"]
};

const CANONICAL_ORDER = ["sku", "name_en", "name_ar", "description", "description_ar", "price", "stock", "min_order", "category_id", "offer_type", "images"];
const NOISE = new Set(["__rowNum__", "_row", "_id", "__proto__", "constructor", "prototype"]);

function isAlias(ofKey, header) {
  const list = HEADER_MAP[ofKey] || [];
  const h = norm(header);
  return list.some((v) => norm(v) === h);
}

/** Map a raw header to a canonical key if it matches any alias */
function canonicalKey(h) {
  for (const key of Object.keys(HEADER_MAP)) {
    if (isAlias(key, h)) return key;
  }
  return null;
}

/** pick the worksheet with the most non-empty rows */
function getBestSheet(workbook) {
  let bestName = workbook.SheetNames[0];
  let bestLen = -1;
  for (const name of workbook.SheetNames) {
    const ws = workbook.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
    const n = rows.filter((r) => Array.isArray(r) && r.some((x) => (x ?? "").toString().trim() !== "")).length;
    if (n > bestLen) {
      bestLen = n;
      bestName = name;
    }
  }
  return bestName;
}

/** detect header row within first N rows */
function detectHeaderRow(rows2d, probe = 15) {
  const limit = Math.min(rows2d.length, probe);
  let bestIdx = 0;
  let bestHits = -1;
  for (let i = 0; i < limit; i++) {
    const row = rows2d[i] || [];
    const hits = row.reduce((acc, cell) => acc + (canonicalKey(cell) ? 1 : 0), 0);
    if (hits > bestHits) {
      bestHits = hits;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Extract embedded images using ExcelJS */
async function extractImagesWithExcelJS(file) {
  try {
    const workbook = new ExcelJS.Workbook();
    const arrayBuffer = await file.arrayBuffer();
    await workbook.xlsx.load(arrayBuffer);
    
    // Get the first worksheet
    const worksheet = workbook.worksheets[0];
    const imagesByRow = {};
    
    // Extract images with more precise row mapping
    worksheet.getImages().forEach((image, imageIndex) => {
      const img = workbook.model.media.find(m => m.index === image.imageId);
      if (!img) return;
      
      // Get image range (which cells it covers)
      const range = image.range;
      
      // Use the top-left corner row, but be more precise
      let rowNumber = range.tl.nativeRow !== undefined ? range.tl.nativeRow : range.tl.row;
      
      // Convert buffer to base64 data URL
      const extension = img.extension || 'png';
      const base64 = img.buffer.toString('base64');
      const dataUrl = `data:image/${extension};base64,${base64}`;
      
      // Create a unique image identifier to avoid duplicates
      const imageId = `${dataUrl.substring(0, 50)}...${imageIndex}`;
      
      console.log(`Image ${imageIndex}: detected at row ${rowNumber}, range:`, range);
      
      if (!imagesByRow[rowNumber]) {
        imagesByRow[rowNumber] = [];
      }
      
      // Check if this exact image is already added to this row
      const isDuplicate = imagesByRow[rowNumber].some(existing => 
        existing.substring(0, 50) === dataUrl.substring(0, 50)
      );
      
      if (!isDuplicate) {
        imagesByRow[rowNumber].push(dataUrl);
      }
    });
    
    console.log('Final imagesByRow mapping:', Object.keys(imagesByRow).map(row => `Row ${row}: ${imagesByRow[row].length} images`));
    
    return imagesByRow;
  } catch (error) {
    console.warn('ExcelJS image extraction failed:', error);
    return {};
  }
}

/** Check if row has meaningful data (not just images) */
function hasValidProductData(record) {
  // Get all keys except image-related fields
  const nonImageKeys = Object.keys(record).filter(key => 
    !['images', 'image', 'photos', 'photo', 'pictures', 'picture', 'imageurl'].includes(key.toLowerCase())
  );
  
  // Check if any non-image field has meaningful content
  return nonImageKeys.some(key => {
    const value = (record[key] ?? "").toString().trim();
    return value !== "";
  });
}

/** Process and validate product data */
function processProductData(record) {
  const processed = { ...record };
  
  // Convert price to number
  if (processed.price) {
    const price = parseFloat(processed.price.toString().replace(/[^\d.-]/g, ''));
    processed.price = isNaN(price) ? 0 : price;
  }
  
  // Convert stock to integer
  if (processed.stock) {
    const stock = parseInt(processed.stock.toString().replace(/[^\d]/g, ''), 10);
    processed.stock = isNaN(stock) ? 0 : stock;
  }
  
  // Convert min_order to integer
  if (processed.min_order) {
    const minOrder = parseInt(processed.min_order.toString().replace(/[^\d]/g, ''), 10);
    processed.min_order = isNaN(minOrder) ? 1 : minOrder;
  }
  
  // Process images - handle comma-separated URLs
  if (processed.images && typeof processed.images === "string") {
    let imageUrls = [];

    // Check if it looks like a list of URLs (contains http and commas)
    if (processed.images.includes("http") && processed.images.includes(",")) {
      imageUrls = processed.images
        .split(/,(?=https?:\/\/|data:image)/) // split only when a new URL starts
        .map((url) => url.trim())
        .filter(Boolean);
    } else {
      // Fallback: simple split
      imageUrls = processed.images
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);
    }

    // Normalize back to string if more than one URL
    processed.images = imageUrls.length > 1 ? imageUrls.join(",") : processed.images;
  }
  
  // Convert category_id to integer if numeric
  if (processed.category_id) {
    const catId = parseInt(processed.category_id.toString().replace(/[^\d]/g, ''), 10);
    if (!isNaN(catId)) {
      processed.category_id = catId;
    }
  }
  
  // Process offer_type - normalize and validate
  if (processed.offer_type) {
    const offerType = processed.offer_type.toString().trim().toLowerCase();
    // Map common variations to standard values
    if (['standard', 'std', 'regular', 'normal', 'عادي', 'قياسي'].includes(offerType)) {
      processed.offer_type = 'standard';
    } else if (['custom', 'customized', 'مخصص', 'مُخصص'].includes(offerType)) {
      processed.offer_type = 'custom';
    } else if (['rent', 'rental', 'lease', 'إيجار', 'تأجير'].includes(offerType)) {
      processed.offer_type = 'rent';
    } else if (['sale', 'sell', 'purchase', 'buy', 'بيع', 'شراء'].includes(offerType)) {
      processed.offer_type = 'sale';
    } else {
      // Keep original value if it doesn't match known patterns
      processed.offer_type = offerType;
    }
  }
  
  return processed;
}

/**
 * Build final rows + columns without duplicates
 */
function finalize(records, headerMap, headersRaw) {
  // 1) Enrich each record with canonical fields
  const enriched = records
    .map((row) => {
      const out = { ...row };

      // direct canonical assignment from aliases
      for (const [orig, val] of Object.entries(row)) {
        const key = headerMap[orig] ?? null;
        if (key && (out[key] == null || out[key] === "")) out[key] = val ?? "";
      }

      // Process product-specific data
      const processed = processProductData(out);

      // drop rows that are completely empty or only have images
      return hasValidProductData(processed) ? processed : null;
    })
    .filter(Boolean);

  // 2) Keep all rows (no deduplication)
  const allRows = enriched;

  // 3) decide which columns to show
  const presentCanon = CANONICAL_ORDER.filter((k) =>
    allRows.some((r) => r[k] != null && `${r[k]}`.trim() !== "")
  );

  const usedAliases = new Set(
    headersRaw.filter((h) => headerMap[h]).map((h) => headerMap[h])
  );

  const extrasRaw = headersRaw.filter((h) => {
    if (!h || NOISE.has(h)) return false;
    const mapped = headerMap[h];
    if (mapped && presentCanon.includes(mapped)) {
      return false;
    }
    if (presentCanon.includes(norm(h))) return false;
    return true;
  });

  const seenLower = new Set();
  const uniqExtras = [];
  for (const h of extrasRaw) {
    const low = norm(h);
    if (!seenLower.has(low)) {
      seenLower.add(low);
      uniqExtras.push(h);
    }
  }

  const columnLabels = {};
  const labelize = (k) =>
    k
      .toString()
      .replace(/[_\-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (m) => m.toUpperCase());

  presentCanon.forEach((k) => (columnLabels[k] = labelize(k)));
  uniqExtras.forEach((h) => (columnLabels[h] = labelize(h)));

  const columns = [...presentCanon, ...uniqExtras];

  const rows = allRows.map((row) => {
    const shaped = {};
    presentCanon.forEach((k) => (shaped[k] = row[k] ?? ""));
    uniqExtras.forEach((h) => (shaped[h] = row[h] ?? ""));
    return shaped;
  });

  return { rows, columns, columnLabels };
}

/* CSV */
function parseCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => (h ?? "").toString().trim(),
      complete: (res) => {
        const data = res.data || [];
        if (!data.length) return resolve({ rows: [], columns: [], columnLabels: {} });

        const headerMap = {};
        Object.keys(data[0] || {}).forEach((k) => {
          const c = canonicalKey(k);
          if (c) headerMap[k] = c;
        });

        const headersRaw = Object.keys(data[0] || {});
        const { rows, columns, columnLabels } = finalize(data, headerMap, headersRaw);
        resolve({ rows, columns, columnLabels });
      },
      error: (err) => reject(err),
    });
  });
}

/* XLS/XLSX with embedded image support */
async function parseXLSX(file) {
  // First, extract images using ExcelJS
  const imagesByRow = await extractImagesWithExcelJS(file);
  
  // Then parse data using XLSX (which is faster for data extraction)
  const ab = await file.arrayBuffer();
  const wb = XLSX.read(ab, { type: "array" });
  const sheetName = getBestSheet(wb);
  const ws = wb.Sheets[sheetName];

  const rows2d = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
  if (!rows2d.length) return { rows: [], columns: [], columnLabels: {} };

  const headerRowIndex = detectHeaderRow(rows2d, 15);
  const headersRaw = (rows2d[headerRowIndex] || []).map((h) => (h ?? "").toString().trim());

  // Find the column index where "Categories Reference" starts
  const categoriesRefIndex = headersRaw.findIndex(h => 
    h.toLowerCase().includes('categories') && h.toLowerCase().includes('reference')
  );

  // Filter headers to exclude Categories Reference columns
  const filteredHeaders = categoriesRefIndex >= 0 
    ? headersRaw.slice(0, categoriesRefIndex)
    : headersRaw;

  // Find image column index
  const imageColumnIndex = filteredHeaders.findIndex(h => isAlias('images', h));
  const imageColumnName = imageColumnIndex >= 0 ? filteredHeaders[imageColumnIndex] : null;

  // build records from header row + subsequent rows
  const dataRows = rows2d.slice(headerRowIndex + 1);
  
  console.log("Header row index:", headerRowIndex);
  console.log("Total rows in sheet:", rows2d.length);
  console.log("Data rows count:", dataRows.length);
  
  // Simple 1:1 row mapping without tracking usage
  
  const records = dataRows.map((arr, dataRowIndex) => {
    const obj = {};
    filteredHeaders.forEach((h, idx) => (obj[h] = arr[idx] ?? ""));
    
    // Calculate Excel row number (0-indexed array to 1-indexed Excel row)
    // dataRowIndex is 0-based index in dataRows array
    // actual row in rows2d array = headerRowIndex + 1 + dataRowIndex
    // Excel row number (1-based) = headerRowIndex + 1 + dataRowIndex + 1
    const actualRowInArray = headerRowIndex + 1 + dataRowIndex;
    const excelRowNumber = actualRowInArray + 1; // Convert to 1-based Excel row
    
    console.log(`Data row ${dataRowIndex}: Excel row ${excelRowNumber}, checking imagesByRow[${excelRowNumber}]`);
    
    // Simple 1:1 mapping: data row N gets images from Excel row N+1
    // Data row 0 -> Excel row 1, Data row 1 -> Excel row 2, etc.
    let foundImages = null;
    let foundAtRow = null;
    
    const targetImageRow = dataRowIndex + 1; // Data row 0 looks for images at row 1, etc.
    
    if (imagesByRow[targetImageRow] && imagesByRow[targetImageRow].length > 0) {
      foundImages = imagesByRow[targetImageRow];
      foundAtRow = targetImageRow;
      console.log(`Direct mapping: Data row ${dataRowIndex} gets images from Excel row ${targetImageRow}`);
    }
    
    if (foundImages) {
      console.log(`Found images at row ${foundAtRow} for data row ${dataRowIndex}:`, foundImages);
      const firstImage = foundImages[0]; // Take only the first image
      
      if (imageColumnName) {
        // Merge with existing images in the column
        const existingImages = obj[imageColumnName] || "";
        obj[imageColumnName] = existingImages 
          ? `${existingImages},${firstImage}`
          : firstImage;
      } else {
        // No image column found, add a new one
        obj["images"] = firstImage;
        if (!filteredHeaders.includes("images")) {
          filteredHeaders.push("images");
        }
      }
    }
    
    return obj;
  });

  const headerMap = {};
  filteredHeaders.forEach((k) => {
    const c = canonicalKey(k);
    if (c) headerMap[k] = c;
  });

  return finalize(records, headerMap, filteredHeaders);
}

/** Public API: return { rows, columns, columnLabels } */
export async function parseProductsFile(file, t = null) {
  const name = (file?.name || "").toLowerCase();
  if (name.endsWith(".csv")) return parseCSV(file);
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) return parseXLSX(file);
  
  // Use translation function if provided, fallback to English
  const errorMessage = t 
    ? t('validation.unsupportedFileType')
    : "Unsupported file type. Please upload CSV or Excel (XLS/XLSX).";
  
  throw new Error(errorMessage);
}